<script lang="ts">
  import Close from '~icons/mdi/close';
  import Send from '~icons/mdi/send';
  import ActionButton from '~/components/ActionButton.svelte';
  import LinkButton from '~/components/LinkButton.svelte';
  import Modal from '~/components/Modal.svelte';
  import { authErrorModal } from './AuthErrorModal.svelte';
  import { signupMutation, type FormInputEvent } from './mutations.svelte';
  import { AuthError } from '@supabase/supabase-js';
  import PasswordFields from './PasswordFields.svelte';
  import { pendingVerification } from './utils.svelte';
  import { validateEmail } from './utils';
  import { signModal } from './Signin.svelte';
  import { verifyEmailModal } from './Verification.svelte';
  import Premium from '~/icons/Premium.svelte';
  import { premiumModal } from '~/modals/PremiumModal.svelte';
  import ValidationError from './ValidationError.svelte';
  import { isApiError } from '~/types';
  import Title from '~/Title.svelte';
  import LogInIcon from '~icons/feather/log-in';
  import { tick } from 'svelte';
  import OauthButtons from './OauthButtons.svelte';

  const signup = signupMutation();

  let email = $state('');
  let password = $state('');
  let agreeToTerms = $state(false);
  let agreeToEmails = $state(true);
  let errors: Partial<
    Record<
      'email' | 'password' | 'confirm' | 'api' | 'terms',
      string | undefined
    >
  > = $state({});
  const formError = $derived(Object.values(errors).filter((e) => e).length > 0);

  const handleReset = () => {
    errors = {};
    signup.reset();
  };

  const handleOpenPremium = () => {
    premiumModal.push('true', 'sign');
  };

  const handleClose = () => {
    handleReset();
    signModal.pop(true);
  };

  const handleValidate = () => {
    if (errors.email) {
      errors.email = validateEmail(email);
    }
  };

  const handleBlur = async (e: FormInputEvent) => {
    errors.email = validateEmail(email);
    // handleReset();
  };

  const handleSignin = () => {
    signModal.replace('in');
  };

  const handleSignup = async (e: SubmitEvent) => {
    e.preventDefault();
    errors.email = validateEmail(email);
    await tick();
    if (formError) return;
    try {
      await signup.mutateAsync({
        email,
        password,
        options: {
          data: {
            subscribe: agreeToEmails,
            username: localStorage.getItem('displayName'),
          },
        },
      });
      pendingVerification.current = email;
      verifyEmailModal.push(email, 'up');
    } catch (error) {
      if (error instanceof AuthError) {
        if (error?.name === 'AuthApiError') {
          authErrorModal.push(error.message);
        }
      } else if (isApiError(error)) {
        errors.api = error.message;
      }
    }
  };
</script>

<Modal open={signModal.value === 'up'} onClose={handleClose} index={6}>
  {#snippet title()}
    <div class="title">
      <Title />
      Create Account
    </div>
  {/snippet}
  {#snippet content()}
    <div class="content">
      <p>
        After signing up, you will be able to subscribe to Gram Jam Premium to
        gain access to exclusive features and puzzles.
      </p>
      {#if !premiumModal.open}
        <LinkButton onClick={handleOpenPremium}>
          <Premium />See Premium Features
        </LinkButton>
      {/if}
      <OauthButtons />
      <form
        id="signup-form"
        class="auth-form"
        method="post"
        novalidate
        onsubmit={handleSignup}
      >
        <ValidationError
          error={errors.api ??
            errors.email ??
            errors.password ??
            errors.confirm}
        />
        <input
          bind:value={email}
          oninput={handleValidate}
          onblur={handleBlur}
          type="email"
          name="email"
          placeholder="email"
          class:invalid={!!errors.email}
          required
          disabled={!(signup.isIdle || signup.isSuccess)}
        />
        <PasswordFields
          onBlur={handleBlur}
          bind:password
          bind:errors
          disabled={!(signup.isIdle || signup.isSuccess)}
        />
        <!--
      <Checkbox
        bind:checked={agreeToEmails}
        label="Send me occasional (<1 per month) email updates, promise no spam!"
      />
-->
      </form>
    </div>
  {/snippet}
  {#snippet controls()}
    <div class="controls">
      <div class="login">
        <LinkButton onClick={handleSignin}>
          already have an account?
          <LogInIcon /> Log in
        </LinkButton>
      </div>
      <div class="signup-container">
        <ActionButton onClick={handleClose}><Close />Back</ActionButton>
        <ActionButton
          form="signup-form"
          type="submit"
          class={formError ? '' : 'form-valid'}
          disabled={formError || !(signup.isIdle || signup.isSuccess)}
        >
          <Send />
          {#if signup.isPending}
            creating...
          {:else if signup.isSuccess}
            created!
          {:else}
            create
          {/if}
        </ActionButton>
      </div>
      <p class="agree">
        by continuing you agree to our <a href="/terms">Terms of Service </a>
        and <a href="/privacy">Privacy Policy</a>
      </p>
    </div>
  {/snippet}
</Modal>

<style lang="scss">
  @use 'sass:color';
  @use '~/theme/theme';
  @use '~/theme/themify' as *;

  .title {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: -0.5em;
    gap: 0.5em;
  }
  .content {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;

    p {
      margin: 0;
      font-size: 0.8rem;
    }
  }
  .agree {
    font-size: 0.75em;
    text-align: center;
    margin: 0;
    a {
      @include themify {
        color: themed('link');
      }
    }
  }
  .signup-container {
    display: grid;
    grid-auto-flow: column;
    gap: 0.5em;
  }
  .controls {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5em;
  }
  .login {
    font-size: 0.75em;
  }
</style>
